import { Inspector, InspectorHost, ElementProposal } from '../../page-insights/inspector';
import { OverlayMode } from '../../overlays/overlay';
import { ControlledIFrameElement } from '@fullstory/controlled-iframe';
import {
  HumanReadable,
  INSPECTOR_HOVER_CLASS,
  INSPECTOR_MATCHED_CLASS,
  parseSelector,
  SelectorForElem,
  SelectorMatch,
  serializeSelector,
  SimilarSelectors,
} from '@fullstory/selectors';
import { ClickStats, gatherIFrames } from '../../page-insights/document_lookup';
import { thouShallNotPass } from '@fullstory/util/events';
import { FsRootDocument } from '@fullstory/playback-web';
import { ScrollModifier, ScrollNavigation, ScrollNavigationEvent } from '../../scroll_navigation';
import { ClickFacet, ClicksBySelector } from '../../page-insights/activitymaps';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import { ComputeElementStatsResponse } from '@fullstory/proto/fs/services/alexa';
import { ElementHighlighter } from '../element-highlighting/element_highlighter';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';

export interface WebInspectorContext {
  activeRootDocFrame(): ControlledIFrameElement | null;
  activeFsRootDocument(): FsRootDocument | null;
}

// The thing running inside the IFrame alongside the DOM being inspected.
export class WebInspector implements Inspector, OverlayMode {
  _leafMostHovered: HTMLElement;
  _wheelHistory: HTMLElement[] = [];
  _doc: Document | null = null;
  _isFF: boolean;
  _unhookers: Array<() => void> = [];
  _hoveredElementHighlighter: ElementHighlighter;
  _selectElementHighlighter: ElementHighlighter;

  constructor(private _context: WebInspectorContext, private _host: InspectorHost) {
    this._isFF = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    this._hoveredElementHighlighter = new ElementHighlighter(_context, {
      borderColor: '#438EE1',
      boxShadowColor: '#EEEEEE',
      className: INSPECTOR_HOVER_CLASS,
    });
    this._selectElementHighlighter = new ElementHighlighter(_context, {
      borderColor: '#F8598B',
      boxShadowColor: '#BBBBBB',
      className: INSPECTOR_MATCHED_CLASS,
    });
  }

  start(): void {
    const frame = this._context.activeRootDocFrame();
    if (!frame) {
      return;
    }
    this._doc = frame.contentDocument;
    this._initWnd(frame.contentWindow, this._doc);
    this._hoveredElementHighlighter.start();
    this._selectElementHighlighter.start();
  }

  stop(): void {
    this._unhookers.forEach(uh => {
      uh();
    });
    this._leafMostHovered = null;
    this._unhookers = [];
    this._doc = null;
    this._hoveredElementHighlighter.stop();
    this._selectElementHighlighter.stop();
  }

  preview(selectorStr: string) {
    this._highlight(selectorStr);
  }

  select(selectorStr: string, selectorArg?: SelectorChain, target?: HTMLElement) {
    let selector = selectorArg;
    const wasTyping = !!selector;
    if (!selector) {
      try {
        selector = parseSelector(selectorStr);
      } catch {
        // Invalid selector. Ignore and fall through to clear any existing highlights
      }
    }
    this._highlight(selectorStr);
    if (selectorStr) {
      const proposal: ElementProposal = {
        fullSelector: <SelectorMatch>{
          Selector: selectorStr,
          HumanReadableSelector: serializeSelector(HumanReadable(selector)),
          Count: this._selectElementHighlighter.highlighters.length,
          Mobile: false,
        },
        similarElements: SimilarSelectors(selector),
        text: target?.innerText,
      };
      this._host.updateProposal(proposal, wasTyping);
    }
  }

  _highlight(selectorStr: string) {
    if (this._leafMostHovered) {
      this._hoveredElementHighlighter.dispose();
      this._leafMostHovered = null;
    }
    this._selectElementHighlighter.highlightBySelectors([selectorStr]);
  }

  // TODO(jaime): InspectMode and PageInsights using the same req/rsp and data types is bad. They should be separated!
  generateStats(rsp: ComputeElementStatsResponse, selector: string) {
    const facet = ClickFacet.fromResponse(rsp);
    if (facet.totalClicks < 1) {
      console.log(`didn't get any click stats`);
      this._host.showStats(facet, [], NativePlatform.NATIVE_PLATFORM_WEB);
      return;
    }
    const clickStats = new ClickStats(
      facet,
      'user',
      new ClicksBySelector(
        parseSelector(selector),
        selector,
        selector,
        '',
        undefined,
        facet.totalClicks,
        facet.totalUsers,
      ),
      true,
    );
    this._host.showStats(facet, clickStats.stats().Stats, NativePlatform.NATIVE_PLATFORM_WEB);
  }

  // stub; this is only used by mobile currently
  getLogicalPageContentRoot() {}

  // Event listeners.
  _onMouseover = evt => {
    const e = <HTMLElement>evt.target;
    if (!e) {
      return;
    }

    this._showHoverAffordance(e);
  };

  _onClick = (evt: Event) => {
    thouShallNotPass(evt);
    if (!this._leafMostHovered) {
      return;
    }

    // TODO(jaime): Some kinda multi select.
    const longMatch = SelectorForElem(this._leafMostHovered);
    const longMatchStr = serializeSelector(longMatch);

    // Note that the fully specified selector might match more than one element sometimes. That's just the
    // nature of how we generate these selectors (we can't use something N-th child here because that ain't what we
    // do in other parts of the system. And it might also not be what we want anyways due to over-specificity).
    this.select(longMatchStr, longMatch, this._leafMostHovered);
  };

  // Adds event listeners to a specific window object.
  _addEventListeners(wnd: Window) {
    const scrollNavigation = new ScrollNavigation(wnd, ScrollModifier.SHIFT, event => {
      switch (event) {
        case ScrollNavigationEvent.SCROLL_UP: {
          this._wheelHistory.push(this._leafMostHovered);
          this._onMouseover({ target: this._leafMostHovered.parentElement });
          break;
        }
        case ScrollNavigationEvent.SCROLL_DOWN: {
          this._onMouseover({ target: this._wheelHistory.pop() });
          break;
        }
      }
    });
    scrollNavigation.start();
    wnd.addEventListener('mouseover', this._onMouseover, { passive: false });

    // This is a swap we have to do in inspect mode.
    wnd.removeEventListener('click', thouShallNotPass, true);
    wnd.addEventListener('click', this._onClick, true);

    this._unhookers.push(() => {
      wnd.removeEventListener('mouseover', this._onMouseover);
      scrollNavigation.stop();

      // Swap it back.
      wnd.removeEventListener('click', this._onClick, true);
      wnd.addEventListener('click', thouShallNotPass, true);
    });
  }

  // Sets up the inspector styles and the event handlers for it, using a DFS
  // along the document's iframes
  _initWnd(wnd: Window, doc: Document | null): Document[] {
    if (!doc || !doc.head) {
      return [];
    }

    const docs = [doc];
    const iframes = gatherIFrames(doc);

    for (const iframe of iframes) {
      const childDocs = this._initWnd(iframe.contentWindow, iframe.contentDocument);
      docs.push(...childDocs);
    }

    this._addEventListeners(wnd);
    const inspectorStyles = this._inspectorStyles(doc);
    doc.head.appendChild(inspectorStyles);
    this._unhookers.push(() => {
      try {
        doc.head.removeChild(inspectorStyles);
      } catch (e) {
        // do nothing
      }
    });
    return docs;
  }

  _showHoverAffordance(hoveredElement: HTMLElement) {
    this._hoveredElementHighlighter.highlightByElement(hoveredElement);
    this._leafMostHovered = hoveredElement;
  }

  // Custom CSS we use for the inspection UI inside the page.
  _inspectorStyles(doc: Document): HTMLStyleElement {
    const style = doc.createElement('style');
    style.textContent = '* { cursor: crosshair !important; }\n';
    return <HTMLStyleElement>style;
  }
}
